import { components } from './generated';

// Re-export typów z generated.ts dla timeline
export type TimelineItemDto = components['schemas']['TimelineItemDto'];
export type MessageTimelineItemDto =
  components['schemas']['MessageTimelineItemDto'];
export type HistoryTimelineItemDto =
  components['schemas']['HistoryTimelineItemDto'];
export type PagedModelTimelineItemDto =
  components['schemas']['PagedModelTimelineItemDto'];
export type PageMetadata = components['schemas']['PageMetadata'];
export type UserDto = components['schemas']['UserDto'];

// Typy dla OrderTimelineDto (status zamówienia + lista pojazdów)
export type OrderTimelineDto = components['schemas']['OrderTimelineDto'];
export type OrderVehicleAccessLinkDto =
  components['schemas']['OrderVehicleAccessLinkDto'];
export type PublicVehicleInfoDto = components['schemas']['PublicVehicleInfoDto'];
export type OrderAddonDto = components['schemas']['OrderAddonDto'];

// Type guards
export function isMessageItem(
  item: TimelineItemDto,
): item is MessageTimelineItemDto & TimelineItemDto {
  return item.itemType === 'MESSAGE';
}

export function isHistoryItem(
  item: TimelineItemDto,
): item is HistoryTimelineItemDto & TimelineItemDto {
  return item.itemType === 'HISTORY_EVENT';
}

// UI-friendly types (mapowane z API)
export interface UITimelineItem {
  id: string;
  timestamp: Date;
  type: 'message' | 'history';
  title: string;
  content: string;
  author?: {
    id: string;
    name: string;
  };
  isSystemAction: boolean;
  editable: boolean;
  // Dodatkowe pola dla history events
  historyType?: HistoryTimelineItemDto['historyType'];
  metadata?: Record<string, unknown>;
  // Dodatkowe pola dla messages
  canEdit?: boolean;
  edited?: boolean;
}

export interface UITimelineData {
  items: UITimelineItem[];
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasMore: boolean;
  };
}

// Typy dla listy pojazdów użytkownika
export interface UserVehicle {
  id: string;
  brandName?: string;
  modelName?: string;
  maskedVin?: string;
  archived: boolean;
  active: boolean;
  createdAt?: Date;
  reportAccessLinkId?: string;
  reportAccessLinkStatus?: OrderVehicleAccessLinkDto['reportAccessLinkStatus'];
  reportAccessLinkExpiresAt?: Date;
  addons: OrderAddonDto[];
}

export interface OrderStatusWithVehicles {
  orderUuid: string;
  externalOrderId?: string;
  orderTimelineStatus?: OrderTimelineDto['orderTimelineStatus'];
  orderTimelineStatusDisplayName?: string;
  orderStatus?: OrderTimelineDto['orderStatus'];
  orderStatusDisplayName?: string;
  brandType?: OrderTimelineDto['brandType'];
  brandContactEmail?: OrderTimelineDto['brandContactEmail'];
  brandContactPhone?: OrderTimelineDto['brandContactPhone'];
  inspectionDate?: Date;
  showPaymentBanner: boolean;
  orderSuspensionMessage?: string;
  clientCanChangeVehicle: boolean;
  addons: OrderAddonDto[];
  vehicles: UserVehicle[];
}

// Type aliases for order statuses
export type OrderTimelineStatus = NonNullable<
  OrderTimelineDto['orderTimelineStatus']
>;
export type OrderStatus = NonNullable<OrderTimelineDto['orderStatus']>;

// Status helper functions
export const isOrderCanceled = (
  status: OrderStatusWithVehicles | null,
): boolean => status?.orderTimelineStatus === 'ORDER_CANCELED';

export const isOrderSuspended = (
  status: OrderStatusWithVehicles | null,
): boolean => status?.orderTimelineStatus === 'ORDER_SUSPENDED';

export const isOrderCompleted = (
  status: OrderStatusWithVehicles | null,
): boolean =>
  status?.orderStatus === 'COMPLETED' ||
  status?.orderTimelineStatus === 'REPORT_READY';

export const isOrderUnpaid = (
  status: OrderStatusWithVehicles | null,
): boolean => status?.orderStatus === 'NEW_UNPAID';

export const shouldShowPaymentBanner = (
  status: OrderStatusWithVehicles | null,
): boolean => status?.showPaymentBanner === true;

export const canChangeVehicle = (
  status: OrderStatusWithVehicles | null,
): boolean => status?.clientCanChangeVehicle === true;

// Status that should show normal order flow
export const isOrderInProgress = (
  status: OrderStatusWithVehicles | null,
): boolean =>
  !isOrderCanceled(status) &&
  !isOrderSuspended(status) &&
  !isOrderCompleted(status);
